/* eslint-disable max-lines-per-function */
import { Fragment, useEffect, useRef, useState } from 'react';

import { X } from 'lucide-react';

import { format } from 'date-fns';

import { cn } from '@/lib/utils.ts';

import { Button } from '@/components/ui/Button.tsx';
import { Checkbox } from '@/components/ui/Checkbox.tsx';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.tsx';

import { useFetchProductByLinkedTool } from '@/components/chat/chat-context-menu/tools/hooks/useFetchProductByLinkedTool';

type GroupedProps = VersionOption & {
  productId?: string;
  productName?: string;
};

const grouped = (data: GroupedProps[]): LocalVersion[] =>
  Object.values(
    data.reduce<Record<string, LocalVersion>>((acc, item) => {
      const key = item.productId || 'unassigned';

      if (!acc[key]) {
        acc[key] = {
          productId: item.productId || null,
          productName: item.productName || 'Unassigned',
          tools: [],
        };
      }

      acc[key].tools.push(item);

      return acc;
    }, {})
  )
    .filter((group) => group.tools.length > 0)
    .sort((productA, productB) => {
      const aUnassigned = productA.productId === null;
      const bUnassigned = productB.productId === null;

      if (aUnassigned === bUnassigned) {
        return 0;
      }

      return aUnassigned ? 1 : -1;
    });

type VersionOption = {
  id: string;
  name: string;
  editedAt: string;
  checked?: boolean;
};

interface LocalVersion {
  productId: string | null;
  productName: string;
  tools: VersionOption[];
}

interface ToolVersionSelectorProps {
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  toolName?: string;
  confirmLabel?: string;
  versions: VersionOption[];
  onToggleVersion?: (id: string, checked: boolean) => void;
  onConfirm?: (selectedIds: string[]) => void;
  onClose?: () => void;
}

const versionIdsKeyFromList = (list: VersionOption[]) =>
  list
    .map((version) => version.id)
    .sort()
    .join('|');

export const ToolVersionSelector = ({
  className,
  open,
  onOpenChange,
  toolName,
  confirmLabel = 'Confirm selected versions',
  versions = [],
  onToggleVersion,
  onConfirm,
  onClose,
}: ToolVersionSelectorProps) => {
  const [localVersions, setLocalVersions] = useState<LocalVersion[]>([]);
  const { fetchProduct } = useFetchProductByLinkedTool();
  const versionsRef = useRef(versions);

  versionsRef.current = versions;

  const versionIdsKey = versionIdsKeyFromList(versions);

  useEffect(() => {
    let cancelled = false;

    const markCancelled = () => {
      cancelled = true;
    };

    if (!open) {
      return markCancelled;
    }

    const updateWithProducts = async () => {
      const snapshot = versionsRef.current;

      const codeWithProduct = await Promise.all(
        snapshot.map(async (version) => {
          const product = await fetchProduct({ toolId: version.id });

          return {
            ...version,
            checked: version.checked ?? false,
            ...product,
          };
        })
      );

      if (cancelled) {
        return;
      }

      setLocalVersions(grouped(codeWithProduct as GroupedProps[]));
    };

    void updateWithProducts();

    return markCancelled;
  }, [open, versionIdsKey, fetchProduct]);

  const selectedVersionIds = localVersions
    .flatMap((group) => group.tools)
    .filter((version) => version.checked)
    .map((version) => version.id);

  const handleCheckedChange =
    (id: string) => (checked: boolean | 'indeterminate') => {
      const nextChecked = Boolean(checked);

      setLocalVersions((prev) =>
        prev.map((group) => ({
          ...group,
          tools: group.tools.map((tool) =>
            tool.id === id ? { ...tool, checked: nextChecked } : tool
          ),
        }))
      );

      onToggleVersion?.(id, nextChecked);
    };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        showOverlay
        showClose={false}
        side="right"
        className={cn(
          'right-0 top-0 h-full w-full flex flex-col gap-4 border border-border-subtle bg-popover p-6 shadow-lg sm:max-w-[640px]',
          className
        )}
      >
        <SheetHeader className="p-0">
          <SheetTitle className="pr-10">
            Choose your {toolName} version
          </SheetTitle>
          <SheetDescription className="pr-10">
            Select the versions you want Stella to focus on for this
            conversation going forward.
          </SheetDescription>
        </SheetHeader>

        <div className="w-full space-y-3 overflow-y-auto pr-2 scrollbar-thin">
          {localVersions.map((group) => (
            <Fragment key={group.productId}>
              <p className="truncate">
                {group.productName !== 'Unassigned' ? (
                  <>Linked to Product {group.productName}</>
                ) : (
                  <>Not linked to any product</>
                )}
              </p>
              {group.tools.map((version) => (
                <label
                  key={version.id}
                  htmlFor={version.id}
                  className="flex cursor-pointer items-start gap-2 text-sm text-foreground"
                >
                  <Checkbox
                    id={version.id}
                    checked={version.checked}
                    onCheckedChange={handleCheckedChange(version.id)}
                    className="mt-0.5"
                  />
                  <div className="flex flex-col gap-1">
                    <span className="leading-none">{version.name}</span>
                    <span className="text-sm text-muted-foreground">
                      Edited:{' '}
                      {format(new Date(version.editedAt), 'dd.MM.yyyy.')}
                    </span>
                  </div>
                </label>
              ))}
            </Fragment>
          ))}
        </div>

        <div className="flex w-full justify-end">
          <Button
            size="sm"
            className="px-4"
            onClick={() => onConfirm?.(selectedVersionIds)}
          >
            {confirmLabel}
          </Button>
        </div>

        <SheetClose asChild>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  );
};
